import type { components } from "./types/schema";

// Re-export useful schema types
export type UmbracoContentItem =
  components["schemas"]["IApiContentResponseModel"];
export type UmbracoMediaItem =
  components["schemas"]["IApiMediaWithCropsResponseModel"];

export interface UmbracoClientConfig {
  baseUrl: string;
  defaultCulture?: string;
  apiKey?: string;
  preview?: boolean;
  /**
   * Next.js Data Cache revalidation window (seconds) for published fetches.
   * Ignored when `preview` is true — preview always fetches fresh (`no-store`)
   * so editors never see stale drafts. Defaults to 180s.
   */
  revalidate?: number;
}

export interface UmbracoQueryOptions {
  culture?: string;
  contentType?: string;
  contentTypes?: string[];
  take?: number;
  skip?: number;
  sort?: string;
  filter?: (item: UmbracoContentItem) => boolean;
}

export interface UmbracoCollectionResponse {
  total: number;
  items: UmbracoContentItem[];
}

export function createUmbracoClient(config: UmbracoClientConfig) {
  const { baseUrl, defaultCulture, apiKey, preview, revalidate } = config;
  const base = baseUrl.replace(/\/$/, "");

  function buildHeaders(culture?: string): Record<string, string> {
    const headers: Record<string, string> = {
      Accept: "application/json",
    };
    const lang = culture || defaultCulture;
    if (lang) headers["Accept-Language"] = lang;
    if (apiKey) headers["Api-Key"] = apiKey;
    if (preview) headers["Preview"] = "true";
    return headers;
  }

  function appendExpand(url: string): string {
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}expand=properties%5B%24all%5D`;
  }

  async function fetchJson<T>(url: string, culture?: string): Promise<T> {
    const expanded = appendExpand(url);
    // Preview must never be cached (editors need live drafts); published content
    // uses the Next.js Data Cache with time-based revalidation so ISR pages
    // don't re-hit Umbraco on every request. (`next` is Next.js' augmentation
    // of `fetch` init — typed loosely here so this package stays framework-free.)
    // EGDE-488: every cached read carries the "umbraco" tag so a publish can
    // flush the whole CMS data cache at once via revalidateTag (see the
    // /app-api/revalidate route) instead of waiting out the time window.
    //
    // EGDE-636: published reads must NEVER pass `cache: "no-store"`. A no-store
    // fetch calls Next's markCurrentScopeAsDynamic, which opts the whole route
    // out of static rendering — the page then answers every request with a fresh
    // render under `Cache-Control: private, no-store`, and its `revalidate`
    // export becomes dead. Content freshness is the webhook's job (publish /
    // unpublish / delete POST /app-api/revalidate, which expires this tag), not
    // a per-request cache bypass.
    const cacheInit: Record<string, unknown> = preview
      ? { cache: "no-store" }
      : { next: { revalidate: revalidate ?? 180, tags: ["umbraco"] } };
    const res = await fetch(expanded, {
      headers: buildHeaders(culture),
      ...cacheInit,
    });
    if (!res.ok) {
      throw new Error(
        `Umbraco API error: ${res.status} ${res.statusText} — ${expanded}`
      );
    }
    return res.json() as Promise<T>;
  }

  function buildContentUrl(params: {
    fetch?: string;
    filter?: string[];
    take?: number;
    skip?: number;
    sort?: string;
  }): string {
    const url = new URL(`${base}/umbraco/delivery/api/v2/content`);
    if (params.fetch) url.searchParams.set("fetch", params.fetch);
    if (params.filter) {
      for (const f of params.filter) {
        url.searchParams.append("filter", f);
      }
    }
    if (params.take !== undefined)
      url.searchParams.set("take", String(params.take));
    if (params.skip !== undefined)
      url.searchParams.set("skip", String(params.skip));
    if (params.sort) {
      for (const s of params.sort.split(",")) {
        url.searchParams.append("sort", s.trim());
      }
    }
    return url.toString();
  }

  return {
    async getByRoute(
      path: string,
      options?: Pick<UmbracoQueryOptions, "culture">
    ): Promise<UmbracoContentItem> {
      const encodedPath = encodeURIComponent(path.replace(/^\//, ""));
      const url = `${base}/umbraco/delivery/api/v2/content/item/${encodedPath}`;
      return fetchJson<UmbracoContentItem>(url, options?.culture);
    },

    async getById(
      id: string,
      options?: Pick<UmbracoQueryOptions, "culture">
    ): Promise<UmbracoContentItem> {
      const url = `${base}/umbraco/delivery/api/v2/content/item/${id}`;
      return fetchJson<UmbracoContentItem>(url, options?.culture);
    },

    async getByIds(
      ids: string[],
      options?: Pick<UmbracoQueryOptions, "culture">
    ): Promise<UmbracoContentItem[]> {
      if (ids.length === 0) return [];
      const url = new URL(`${base}/umbraco/delivery/api/v2/content/items`);
      for (const id of ids) {
        url.searchParams.append("id", id);
      }
      return fetchJson<UmbracoContentItem[]>(url.toString(), options?.culture);
    },

    async getByPaths(
      paths: string[],
      options?: Pick<UmbracoQueryOptions, "culture">
    ): Promise<UmbracoContentItem[]> {
      if (paths.length === 0) return [];
      const results = await Promise.all(
        paths.map((p) => {
          const encodedPath = encodeURIComponent(p.replace(/^\//, ""));
          const url = `${base}/umbraco/delivery/api/v2/content/item/${encodedPath}`;
          return fetchJson<UmbracoContentItem>(url, options?.culture);
        })
      );
      return results;
    },

    async getDescendants(
      path: string,
      options?: UmbracoQueryOptions
    ): Promise<UmbracoCollectionResponse> {
      const filters: string[] = [];
      if (options?.contentType) {
        filters.push(`contentType:${options.contentType}`);
      }
      if (options?.contentTypes?.length) {
        for (const ct of options.contentTypes) {
          filters.push(`contentType:${ct}`);
        }
      }

      const url = buildContentUrl({
        fetch: `descendants:${path}`,
        filter: filters.length > 0 ? filters : undefined,
        take: options?.take,
        skip: options?.skip,
        sort: options?.sort,
      });

      const data = await fetchJson<UmbracoCollectionResponse>(
        url,
        options?.culture
      );

      if (options?.filter) {
        const filtered = data.items.filter(options.filter);
        return { total: filtered.length, items: filtered };
      }

      return data;
    },

    async getChildren(
      path: string,
      options?: UmbracoQueryOptions
    ): Promise<UmbracoCollectionResponse> {
      const filters: string[] = [];
      if (options?.contentType) {
        filters.push(`contentType:${options.contentType}`);
      }
      if (options?.contentTypes?.length) {
        for (const ct of options.contentTypes) {
          filters.push(`contentType:${ct}`);
        }
      }

      const url = buildContentUrl({
        fetch: `children:${path}`,
        filter: filters.length > 0 ? filters : undefined,
        take: options?.take,
        skip: options?.skip,
        sort: options?.sort,
      });

      const data = await fetchJson<UmbracoCollectionResponse>(
        url,
        options?.culture
      );

      if (options?.filter) {
        const filtered = data.items.filter(options.filter);
        return { total: filtered.length, items: filtered };
      }

      return data;
    },

    async getMedia(
      id: string,
      options?: Pick<UmbracoQueryOptions, "culture">
    ): Promise<UmbracoMediaItem> {
      const url = `${base}/umbraco/delivery/api/v2/media/item/${id}`;
      return fetchJson<UmbracoMediaItem>(url, options?.culture);
    },

    async getMediaItems(
      ids: string[],
      options?: Pick<UmbracoQueryOptions, "culture">
    ): Promise<UmbracoMediaItem[]> {
      if (ids.length === 0) return [];
      const url = new URL(`${base}/umbraco/delivery/api/v2/media/items`);
      for (const id of ids) {
        url.searchParams.append("id", id);
      }
      return fetchJson<UmbracoMediaItem[]>(url.toString(), options?.culture);
    },
  };
}

export type UmbracoClient = ReturnType<typeof createUmbracoClient>;
